import React, { useState, useCallback } from "react";
import { StyleSheet, View, TouchableOpacity, Text, Clipboard } from "react-native";
import { useFocusEffect } from "expo-router";
import { FontAwesome, MaterialIcons } from "@expo/vector-icons";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption, type ThemedListViewMode } from "@/components/ThemedList";

import { useFirebase } from "@components/firebase/useFirebase";
import { useAuth } from "@components/auth/useAuth";
import { getMagicLinksFunc } from "@/services/firebase/user-functions";
import { MagicLink } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import Constants from "expo-constants";

/**
 * Converts a variety of timestamp/date formats into a millisecond timestamp.
 * Supports milliseconds, seconds, string dates, and Firestore Timestamp objects.
 *
 * @param val - The raw timestamp/date value to normalize.
 * @returns The normalized millisecond timestamp, or 0 if invalid.
 */
function getTimestampMs(val: any): number {
  if (!val) return 0;
  if (typeof val === "number") {
    return val < 99999999999 ? val * 1000 : val;
  }
  if (typeof val === "string") {
    return new Date(val).getTime();
  }
  if (typeof val === "object") {
    if (typeof val.toDate === "function") {
      return val.toDate().getTime();
    }
    const sec = val._seconds ?? val.seconds;
    if (typeof sec === "number") {
      return sec * 1000;
    }
  }
  return 0;
}

function getStatus(item: MagicLink): "active" | "used" | "expired" {
  if (item.used) return "used";
  const expiryTime = getTimestampMs(item.expiresAt);
  if (expiryTime && Date.now() > expiryTime) return "expired";
  return "active";
}

function formatDateTime(val: any): string {
  const ms = getTimestampMs(val);
  return ms ? new Date(ms).toLocaleString() : "N/A";
}

const MAGIC_LINK_SORT_OPTIONS: ISortOption<MagicLink>[] = [
  { key: "createdAt", label: "Date Sent", getValue: (l) => getTimestampMs(l.createdAt) },
  { key: "expiresAt", label: "Expiry Date", getValue: (l) => getTimestampMs(l.expiresAt) },
  { key: "email", label: "Recipient Email", getValue: (l) => l.email || "" },
  { key: "displayName", label: "Recipient Name", getValue: (l) => l.displayName || "" },
  { key: "linkType", label: "Link Type", getValue: (l) => l.linkType || "" },
  { key: "status", label: "Status", getValue: (l) => getStatus(l) },
];

const MAGIC_LINK_GROUP_OPTIONS: IGroupOption<MagicLink>[] = [
  {
    key: "status",
    label: "Status",
    getGroupValue: (l) => {
      const s = getStatus(l);
      return s === "active" ? "Active Links" : s === "used" ? "Used Links" : "Expired Links";
    },
  },
  {
    key: "linkType",
    label: "Link Type",
    getGroupValue: (l) => (l.linkType === "invitation" ? "Invitations" : "Password Resets"),
  },
  {
    key: "month",
    label: "Sent Month",
    getGroupValue: (l) => {
      const ms = getTimestampMs(l.createdAt);
      if (!ms) return "Unknown Date";
      return new Date(ms).toLocaleDateString("en-US", { year: "numeric", month: "long" });
    },
  },
];

/**
 * Screen displaying all sent magic links in the system.
 * Accessible only by administrators.
 * Supports segmented view modes, sorting, grouping, searching, and copying.
 */
export default function MagicLinkAdminScreen() {
  const services = useFirebase();
  useAuth(true);

  const [magicLinks, setMagicLinks] = useState<MagicLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const activeTint = useThemeColor({}, "tint");

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await getMagicLinksFunc(services);
      setMagicLinks(data || []);
    } catch (error) {
      console.error("Error fetching magic links:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching the magic links.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const buildUrl = (linkId: string, linkType: "invitation" | "password_reset"): string => {
    const vars = Constants.expoConfig?.extra!;
    const projectId = vars.PROJECT_ID!;
    const region = vars.REGION || "europe-north1";
    const functionName = linkType === "invitation" ? "acceptInvitationFunc" : "acceptPasswordResetFunc";
    const domain = typeof window !== "undefined" && window.location ? window.location.host : "memories-dev-247.web.app";

    if (vars.env === "LOCAL" && vars.FUNCTIONS_EMULATOR_HOST) {
      return `${vars.FUNCTIONS_EMULATOR_HOST}/${projectId}/${region}/${functionName}?id=${linkId}&domain=${encodeURIComponent(domain)}`;
    }

    return `https://${region}-${projectId}.cloudfunctions.net/${functionName}?id=${linkId}&domain=${encodeURIComponent(domain)}`;
  };

  const handleCopy = (text: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      Clipboard.setString(text);
    }
    CustomAlert.alert("Copied", "Link copied to clipboard!");
  };

  const getStatusStyle = (status: "active" | "used" | "expired") => {
    switch (status) {
      case "active":
        return {
          bg: "rgba(16, 185, 129, 0.1)",
          border: "rgba(16, 185, 129, 0.3)",
          text: "#10B981",
        };
      case "used":
        return {
          bg: "rgba(107, 114, 128, 0.1)",
          border: "rgba(107, 114, 128, 0.3)",
          text: "#6B7280",
        };
      case "expired":
        return {
          bg: "rgba(239, 68, 68, 0.1)",
          border: "rgba(239, 68, 68, 0.3)",
          text: "#EF4444",
        };
    }
  };

  const renderItem = (
    item: MagicLink,
    _index: number,
    _isHovered: boolean,
    viewMode: ThemedListViewMode = "medium"
  ) => {
    const status = getStatus(item);
    const statusColors = getStatusStyle(status);
    const fullUrl = buildUrl(item.id, item.linkType);

    if (viewMode === "compact") {
      return (
        <TouchableOpacity
          style={[styles.compactModeCard, { backgroundColor: cardBgColor, borderColor }]}
          onPress={() => handleCopy(fullUrl)}
          activeOpacity={0.7}
        >
          <ThemedText type="defaultSemiBold" style={styles.compactModeTitle} numberOfLines={1}>
            {item.email}
          </ThemedText>
        </TouchableOpacity>
      );
    }

    if (viewMode === "details") {
      return (
        <View style={[styles.detailsRowCard, { backgroundColor: cardBgColor, borderColor }]}>
          <View style={styles.detailsRowLeft}>
            <MaterialIcons
              name={item.linkType === "invitation" ? "mail-outline" : "lock-outline"}
              size={18}
              color={textMuted}
            />
            <View style={styles.detailsRowUser}>
              <ThemedText type="defaultSemiBold" style={styles.emailText} numberOfLines={1}>
                {item.email}
              </ThemedText>
              {item.displayName ? (
                <ThemedText style={[styles.detailsSubText, { color: textMuted }]} numberOfLines={1}>
                  {item.displayName}
                </ThemedText>
              ) : null}
            </View>
          </View>

          <View style={styles.detailsRowMiddle}>
            <ThemedText style={[styles.detailsSubText, { color: textMuted }]} numberOfLines={1}>
              Sent: {formatDateTime(item.createdAt)}
            </ThemedText>
            <ThemedText style={[styles.detailsSubText, { color: textMuted }]} numberOfLines={1}>
              Exp: {formatDateTime(item.expiresAt)}
            </ThemedText>
          </View>

          <View style={styles.detailsRowRight}>
            <View style={[styles.statusBadge, { backgroundColor: statusColors.bg, borderColor: statusColors.border }]}>
              <Text style={[styles.statusText, { color: statusColors.text }]}>{status}</Text>
            </View>
            <TouchableOpacity style={[styles.copyButtonCompact, { backgroundColor: activeTint }]} onPress={() => handleCopy(fullUrl)}>
              <FontAwesome name="copy" size={11} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    return (
      <View style={[styles.card, { backgroundColor: cardBgColor, borderColor }]}>
        {/* Header info */}
        <View style={styles.cardHeader}>
          <View style={styles.recipientInfo}>
            <ThemedText type="defaultSemiBold" style={styles.emailText} numberOfLines={1}>
              {item.email}
            </ThemedText>
            {item.displayName && <ThemedText style={[styles.nameText, { color: textMuted }]}>{item.displayName}</ThemedText>}
          </View>
          <View style={[styles.statusBadge, { backgroundColor: statusColors.bg, borderColor: statusColors.border }]}>
            <Text style={[styles.statusText, { color: statusColors.text }]}>{status}</Text>
          </View>
        </View>

        {/* Details */}
        <View style={styles.detailsContainer}>
          <View style={styles.detailRow}>
            <MaterialIcons name="mail-outline" size={14} color={textMuted} />
            <ThemedText style={[styles.detailLabel, { color: textMuted }]}>TYPE:</ThemedText>
            <ThemedText style={styles.detailValue}>{item.linkType === "invitation" ? "Invitation" : "Password Reset"}</ThemedText>
          </View>
          <View style={styles.detailRow}>
            <MaterialIcons name="date-range" size={14} color={textMuted} />
            <ThemedText style={[styles.detailLabel, { color: textMuted }]}>SENT:</ThemedText>
            <ThemedText style={styles.detailValue}>{formatDateTime(item.createdAt)}</ThemedText>
          </View>
          <View style={styles.detailRow}>
            <MaterialIcons name="timer" size={14} color={textMuted} />
            <ThemedText style={[styles.detailLabel, { color: textMuted }]}>EXPIRES:</ThemedText>
            <ThemedText style={styles.detailValue}>{formatDateTime(item.expiresAt)}</ThemedText>
          </View>
        </View>

        {/* URL box */}
        <View style={[styles.urlContainer, { borderColor }]}>
          <ThemedText numberOfLines={1} ellipsizeMode="middle" style={styles.urlText}>
            {fullUrl}
          </ThemedText>
          <TouchableOpacity style={[styles.copyButton, { backgroundColor: activeTint }]} onPress={() => handleCopy(fullUrl)}>
            <FontAwesome name="copy" size={12} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <ThemedScreen title="Magic Links" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${magicLinks.length} TOTAL ${magicLinks.length === 1 ? "LINK" : "LINKS"}`}
        </ThemedText>

        <ThemedList<MagicLink>
          data={magicLinks}
          sortOptions={MAGIC_LINK_SORT_OPTIONS}
          defaultSortKey="createdAt"
          defaultSortDirection="desc"
          groupOptions={MAGIC_LINK_GROUP_OPTIONS}
          defaultGroupKey="status"
          persistenceKey="admin-magic-links"
          pageSize={24}
          filterKeys={["email", "displayName", "linkType"]}
          filterPlaceholder="Search magic links by email, name, type..."
          loading={loading}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
          emptyText="No magic links found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, index, isHovered, viewMode) => renderItem(item, index, isHovered, viewMode)}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
    gap: 8,
  },
  card: {
    padding: 14,
    borderRadius: 0,
    borderWidth: 1,
    gap: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  recipientInfo: {
    flex: 1,
    gap: 2,
  },
  emailText: {
    fontSize: 14,
  },
  nameText: {
    fontSize: 11,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 0,
    borderWidth: 1,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  detailsContainer: {
    gap: 4,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  detailLabel: {
    fontSize: 10,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  detailValue: {
    fontSize: 11,
  },
  urlContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 0,
    paddingLeft: 8,
    height: 34,
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,0.015)",
  },
  urlText: {
    flex: 1,
    fontSize: 11,
    fontFamily: "monospace",
    opacity: 0.7,
    marginRight: 6,
  },
  copyButton: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 0,
  },
  compactModeCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 0,
    paddingHorizontal: 4,
    paddingVertical: 4,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 30,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  compactModeTitle: {
    fontSize: 10,
    textAlign: "center",
  },
  detailsRowCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: 0,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  detailsRowLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 2,
    gap: 8,
  },
  detailsRowUser: {
    flex: 1,
  },
  detailsRowMiddle: {
    flex: 1.5,
    paddingHorizontal: 8,
  },
  detailsSubText: {
    fontSize: 10,
    marginTop: 1,
  },
  detailsRowRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  copyButtonCompact: {
    width: 26,
    height: 26,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 0,
  },
});
